import React, { useState, useEffect } from 'react';
import { FiX, FiPlus } from 'react-icons/fi';
import EmojiPicker from './EmojiPicker';
import { PersonalMovie, CreatePersonalMovieData, UpdatePersonalMovieData } from '../services/personalMoviesService';

interface AddPersonalMovieDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: CreatePersonalMovieData | UpdatePersonalMovieData) => Promise<void>;
  editingMovie?: PersonalMovie | null;
}

const AddPersonalMovieDialog: React.FC<AddPersonalMovieDialogProps> = ({
  isOpen,
  onClose,
  onSave,
  editingMovie
}) => {
  const [title, setTitle] = useState('');
  const [color, setColor] = useState('#3b82f6');
  const [emoji, setEmoji] = useState('🎬');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset form when dialog opens/closes or when editing movie changes
  useEffect(() => {
    if (isOpen) {
      if (editingMovie) {
        setTitle(editingMovie.title);
        setColor(editingMovie.color);
        setEmoji(editingMovie.emoji);
      } else {
        setTitle('');
        setColor('#3b82f6');
        setEmoji('🎬');
      }
      setShowEmojiPicker(false);
      setError(null); // Clear any previous errors
    }
  }, [isOpen, editingMovie]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;

    setIsLoading(true);
    setError(null); // Clear any previous errors
    
    try {
      await onSave({
        title: title.trim(),
        color,
        emoji
      });
      // Don't call onClose() here - let the parent handle it after successful save
    } catch (error) {
      console.error('Failed to save personal movie:', error);
      const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  // Create preview gradient style
  const previewGradientStyle = {
    background: `linear-gradient(135deg, ${color} 0%, ${color}dd 50%, ${color}bb 100%)`,
  };

  return (
    <div 
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={handleBackdropClick}
    >
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-heading font-bold text-secondary-dark">
            {editingMovie ? 'Edit Personal Movie' : 'Add Personal Movie'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            aria-label="Close dialog"
          >
            <FiX className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}
          {/* Live Preview */}
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Preview</h3>
            <div className="flex justify-center">
              <div className="w-32 h-48 rounded-lg shadow-lg overflow-hidden">
                <div 
                  className="w-full h-full flex flex-col items-center justify-center text-white relative"
                  style={previewGradientStyle}
                >
                  {/* Subtle overlay for depth */}
                  <div className="absolute inset-0 bg-gradient-to-br from-white/10 via-transparent to-black/20"></div>
                  
                  {/* Content */}
                  <div className="relative z-10 text-center p-2 flex flex-col items-center justify-center h-full">
                    {/* Emoji */}
                    <div className="text-3xl mb-2 drop-shadow-lg">
                      {emoji}
                    </div>
                    
                    {/* Title */}
                    <h4 className="font-heading font-bold text-sm text-center leading-tight drop-shadow-md">
                      {title || 'Movie Title'}
                    </h4>
                  </div>

                  {/* Decorative elements */}
                  <div className="absolute top-0 left-0 w-full h-full pointer-events-none">
                    <div className="absolute top-0 left-0 right-0 h-1/3 bg-gradient-to-b from-white/20 to-transparent"></div>
                    <div className="absolute bottom-0 left-0 right-0 h-1/4 bg-gradient-to-t from-black/30 to-transparent"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Title Input */}
            <div>
              <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
                Movie Title *
              </label>
              <input
                type="text"
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder="Enter movie title"
                required
                maxLength={50}
              />
            </div>

            {/* Color Picker */}
            <div>
              <label htmlFor="color" className="block text-sm font-medium text-gray-700 mb-1">
                Background Color
              </label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  id="color"
                  value={color}
                  onChange={(e) => setColor(e.target.value)}
                  className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
                />
                <input
                  type="text"
                  value={color}
                  onChange={(e) => setColor(e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="#3b82f6"
                />
              </div>
            </div>

            {/* Emoji Picker */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Emoji
              </label>
              <div className="space-y-2">
                <button
                  type="button"
                  onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                  className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                >
                  <span className="text-2xl">{emoji}</span>
                  <span className="text-sm text-gray-600">Click to change</span>
                </button>
                
                {showEmojiPicker && (
                  <EmojiPicker
                    selectedEmoji={emoji}
                    onEmojiSelect={(selectedEmoji) => {
                      setEmoji(selectedEmoji);
                      setShowEmojiPicker(false);
                    }}
                  />
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
                disabled={isLoading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="flex-1 px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                disabled={isLoading || !title.trim()}
              >
                {isLoading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <FiPlus className="w-4 h-4" />
                    {editingMovie ? 'Update' : 'Add'} Movie
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddPersonalMovieDialog;